import {
	SESSION_ABSOLUTE_EXPIRES_IN_HEADER,
	SESSION_END_REASON_HEADER,
	SESSION_IDLE_EXPIRES_IN_HEADER,
	SessionEndReason,
	parseSessionEndReason,
} from '@dealstack/shared/lib/common/session/sessionContract';

// The server's view of when the session ends, kept fresh from the headers on every
// authenticated response, so the countdown is driven by the clock that enforces it.
//
// The server sends time *remaining*; we anchor it against this device's clock on arrival.
// That way a device whose clock is offset from the server's still counts down correctly —
// only the clock's rate matters, and rate error over an idle window is negligible.

export interface SessionDeadline {
	idleExpiresAt: number;
	absoluteExpiresAt: number;
}

type Listener = (deadline: SessionDeadline | null) => void;

/**
 * How far two deadlines may differ and still be treated as the same one. Every response
 * re-anchors against `Date.now()`, so identical server state arrives jittered by request
 * latency; without this the store would publish on every response and restart the
 * consumer's ticker each time.
 */
export const DEADLINE_EQUALITY_TOLERANCE_MS = 5_000;

let current: SessionDeadline | null = null;
const listeners = new Set<Listener>();

const parseSeconds = (value: string | null): number | null => {
	if (value === null) {
		return null;
	}
	const parsed = Number(value);

	return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const isSameDeadline = (a: SessionDeadline | null, b: SessionDeadline | null): boolean => {
	if (a === null || b === null) {
		return a === b;
	}

	return (
		Math.abs(a.idleExpiresAt - b.idleExpiresAt) <= DEADLINE_EQUALITY_TOLERANCE_MS &&
		Math.abs(a.absoluteExpiresAt - b.absoluteExpiresAt) <= DEADLINE_EQUALITY_TOLERANCE_MS
	);
};

const publish = (): void => listeners.forEach((listener) => listener(current));

export const getSessionDeadline = (): SessionDeadline | null => current;

/** Publishes only on a real move, so subscribers are not woken by every response. */
export const setSessionDeadline = (deadline: SessionDeadline | null): void => {
	if (isSameDeadline(current, deadline)) {
		return;
	}
	current = deadline;
	publish();
};

export const subscribeToSessionDeadline = (listener: Listener): (() => void) => {
	listeners.add(listener);

	return () => {
		listeners.delete(listener);
	};
};

/** Turns the server's "expires in N seconds" into a local instant. */
export const anchorSessionDeadline = ({
	idleExpiresInSeconds,
	absoluteExpiresInSeconds,
}: {
	idleExpiresInSeconds: number;
	absoluteExpiresInSeconds: number;
}): SessionDeadline => {
	const now = Date.now();

	return {
		idleExpiresAt: now + idleExpiresInSeconds * 1000,
		absoluteExpiresAt: now + absoluteExpiresInSeconds * 1000,
	};
};

/** Responses with no session headers (public endpoints, assets) leave the deadline alone. */
export const readSessionDeadlineFromHeaders = (headers: Headers): void => {
	const idleExpiresInSeconds = parseSeconds(headers.get(SESSION_IDLE_EXPIRES_IN_HEADER));
	const absoluteExpiresInSeconds = parseSeconds(headers.get(SESSION_ABSOLUTE_EXPIRES_IN_HEADER));

	if (idleExpiresInSeconds === null || absoluteExpiresInSeconds === null) {
		return;
	}

	setSessionDeadline(anchorSessionDeadline({ idleExpiresInSeconds, absoluteExpiresInSeconds }));
};

export const readSessionEndReasonFromHeaders = (headers: Headers): SessionEndReason | null =>
	parseSessionEndReason(headers.get(SESSION_END_REASON_HEADER));

export const clearSessionDeadline = (): void => setSessionDeadline(null);
